// Shared chat thread used by both the MARS-user messages page and the
// affiliate portal. MARS-blue theme. Features: text + photo messages
// (upload via /api/affiliate-messaging/upload), edit and delete of own
// messages within a 15-minute window, read receipts, and 5s polling (full
// window refresh so edits/deletes from the other side appear too; paused
// while the tab is hidden).
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Send, Loader2, Image as ImageIcon, Pencil, Trash2, Check, X } from 'lucide-react';
import { onChatEvent } from './chatSocket';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Real-time delivery comes over the socket; polling is only a slow fallback
// for missed events (reconnects, proxy issues).
const POLL_MS = 15000;
const EDIT_WINDOW_MS = 15 * 60 * 1000; // keep in sync with the backend

const ConversationThread = ({ conversationId, title, subtitle, onActivity, actorRole = 'user', headerAction = null }) => {
  const [messages, setMessages] = useState([]);
  const [mySide, setMySide] = useState(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const onActivityRef = useRef(onActivity);
  onActivityRef.current = onActivity;

  const apiBase = `${getApiUrl()}/api/affiliate-messaging/conversations/${conversationId}`;
  // Disambiguates which identity acts when user+affiliate sessions coexist
  // in the same browser.
  const actorHeader = { 'X-Mars-Actor': actorRole };

  const markRead = useCallback(async () => {
    try {
      await fetch(`${apiBase}/read`, { method: 'POST', credentials: 'include', headers: { 'X-Mars-Actor': actorRole } });
      if (onActivityRef.current) onActivityRef.current();
    } catch (_) { /* non-fatal */ }
  }, [apiBase, actorRole]);

  // Full-window refresh (limit 300) so edits/deletes propagate, not just new
  // messages. Optimistic placeholders (tmp-*) are kept on top of server state.
  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const response = await fetch(`${apiBase}/messages?afterId=0&limit=300`, { credentials: 'include', headers: { 'X-Mars-Actor': actorRole } });
      if (!response.ok) {
        if (initial) setError('Could not load this conversation.');
        return;
      }
      const data = await response.json();
      if (!data.success) return;
      setMySide(data.side);
      setMessages(prev => {
        const pending = prev.filter(m => String(m.id).startsWith('tmp-'));
        return [...data.messages, ...pending];
      });
      const hasUnreadIncoming = data.messages.some(m => m.sender_role !== data.side && !m.read_at);
      if (hasUnreadIncoming && document.visibilityState === 'visible') {
        markRead();
      }
    } catch (_) {
      if (initial) setError('Could not load this conversation.');
    } finally {
      if (initial) setLoading(false);
    }
  }, [apiBase, markRead, actorRole]);

  useEffect(() => {
    setMessages([]);
    setError('');
    setLoading(true);
    setEditingId(null);
    stickToBottomRef.current = true;
    fetchMessages(true);
    markRead();

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchMessages(false);
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [conversationId, fetchMessages, markRead]);

  // Instant updates pushed over the socket for this conversation.
  useEffect(() => {
    const unsubscribe = onChatEvent(actorRole, (event) => {
      if (event.conversationId !== conversationId) return;
      if (event.type === 'message:new') {
        setMessages(prev => prev.some(m => m.id === event.message.id) ? prev : [...prev, event.message]);
        if (event.message.sender_role !== actorRole && document.visibilityState === 'visible') {
          markRead();
        }
      } else if (event.type === 'message:updated') {
        setMessages(prev => prev.map(m => (m.id === event.message.id ? event.message : m)));
      } else if (event.type === 'message:deleted') {
        setMessages(prev => prev.map(m => (m.id === event.messageId
          ? { ...m, deleted_at: new Date().toISOString(), body: '', attachment_url: null }
          : m)));
      }
    });
    return unsubscribe;
  }, [conversationId, actorRole, markRead]);

  useEffect(() => {
    if (stickToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  const postMessage = async (payload, optimistic) => {
    setMessages(prev => [...prev, optimistic]);
    try {
      const response = await fetch(`${apiBase}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...actorHeader },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok && data.success) {
        // The socket may have already delivered the confirmed message — drop
        // the optimistic placeholder and dedupe by id.
        setMessages(prev => {
          const withoutTmp = prev.filter(m => m.id !== optimistic.id);
          return withoutTmp.some(m => m.id === data.message.id) ? withoutTmp : [...withoutTmp, data.message];
        });
        if (onActivityRef.current) onActivityRef.current();
        return true;
      }
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setError(data.message || 'Message failed to send.');
      return false;
    } catch (_) {
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setError('Message failed to send. Check your connection.');
      return false;
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setError('');
    setDraft('');

    const ok = await postMessage(
      { body: text },
      { id: `tmp-${Date.now()}`, sender_role: mySide, body: text, message_type: 'text', created_at: new Date().toISOString() }
    );
    if (!ok) setDraft(text);
    setSending(false);
  };

  const handlePickPhoto = () => fileInputRef.current?.click();

  const handlePhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file || uploading) return;
    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadResponse = await fetch(`${getApiUrl()}/api/affiliate-messaging/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: actorHeader,
        body: formData
      });
      const uploadData = await uploadResponse.json();
      if (!uploadResponse.ok || !uploadData.success) {
        setError(uploadData.message || 'Photo upload failed.');
        return;
      }

      const caption = draft.trim();
      setDraft('');
      await postMessage(
        { body: caption, attachmentUrl: uploadData.url, attachmentName: uploadData.name },
        {
          id: `tmp-${Date.now()}`, sender_role: mySide, body: caption, message_type: 'image',
          attachment_url: uploadData.url, attachment_name: uploadData.name,
          created_at: new Date().toISOString()
        }
      );
    } catch (_) {
      setError('Photo upload failed. Check your connection.');
    } finally {
      setUploading(false);
    }
  };

  const canModify = (m) =>
    m.sender_role === mySide &&
    !m.deleted_at &&
    !String(m.id).startsWith('tmp-') &&
    Date.now() - new Date(m.created_at).getTime() < EDIT_WINDOW_MS;

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditDraft(m.body);
    setError('');
  };

  const saveEdit = async (messageId) => {
    const text = editDraft.trim();
    if (!text || savingEdit) return;
    setSavingEdit(true);
    try {
      const response = await fetch(`${apiBase}/messages/${messageId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...actorHeader },
        credentials: 'include',
        body: JSON.stringify({ body: text })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessages(prev => prev.map(m => (m.id === messageId ? data.message : m)));
        setEditingId(null);
      } else {
        setError(data.message || 'Could not edit the message.');
      }
    } catch (_) {
      setError('Could not edit the message. Check your connection.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      const response = await fetch(`${apiBase}/messages/${messageId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: actorHeader
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setMessages(prev => prev.map(m => (m.id === messageId
          ? { ...m, deleted_at: new Date().toISOString(), body: '', attachment_url: null }
          : m)));
        if (onActivityRef.current) onActivityRef.current();
      } else {
        setError(data.message || 'Could not delete the message.');
      }
    } catch (_) {
      setError('Could not delete the message. Check your connection.');
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Thread header */}
      <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{title}</p>
          {subtitle && <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{subtitle}</p>}
        </div>
        {headerAction}
      </div>

      {/* Messages */}
      <div onScroll={handleScroll} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50 dark:bg-slate-950">
        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
          </div>
        )}
        {!loading && messages.length === 0 && !error && (
          <p className="text-center text-sm text-gray-400 py-10">No messages yet. Say hello!</p>
        )}
        {error && <p className="text-center text-sm text-red-500 py-2">{error}</p>}
        {messages.map((m) => {
          const mine = m.sender_role === mySide;
          const isEditing = editingId === m.id;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`group relative max-w-[75%] ${mine ? 'items-end' : 'items-start'}`}>
                <div className={`rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.deleted_at
                    ? 'bg-gray-100 dark:bg-slate-800 text-gray-400 italic border border-gray-200 dark:border-slate-700'
                    : mine
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-br-sm'
                      : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-100 border border-gray-200 dark:border-slate-700 rounded-bl-sm'
                }`}>
                  {m.deleted_at ? (
                    <p>This message was deleted</p>
                  ) : isEditing ? (
                    <div className="min-w-[220px]">
                      <textarea
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        rows={2}
                        maxLength={5000}
                        autoFocus
                        className="w-full resize-none px-2 py-1.5 rounded-lg text-sm text-gray-800 bg-white border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveEdit(m.id); }
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <div className="flex justify-end gap-2 mt-1.5">
                        <button onClick={() => setEditingId(null)} className="p-1 rounded bg-white/20 hover:bg-white/30" title="Cancel">
                          <X className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => saveEdit(m.id)} disabled={savingEdit} className="p-1 rounded bg-white/20 hover:bg-white/30" title="Save">
                          {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {m.attachment_url && (
                        <a href={`${getApiUrl()}${m.attachment_url}`} target="_blank" rel="noopener noreferrer" className="block mb-1">
                          <img
                            src={`${getApiUrl()}${m.attachment_url}`}
                            alt={m.attachment_name || 'Shared photo'}
                            className="rounded-lg max-h-64 max-w-full object-cover"
                            loading="lazy"
                          />
                        </a>
                      )}
                      {m.body && <p>{m.body}</p>}
                      <p className={`text-[10px] mt-1 ${mine && !m.deleted_at ? 'text-blue-200' : 'text-gray-400'}`}>
                        {formatTime(m.created_at)}{m.edited_at ? ' · edited' : ''}
                      </p>
                    </>
                  )}
                </div>

                {/* Edit/delete actions — own messages, within the 15-min window */}
                {canModify(m) && !isEditing && (
                  <div className={`absolute top-1 ${mine ? '-left-16' : '-right-16'} hidden group-hover:flex items-center gap-1`}>
                    {!m.attachment_url && (
                      <button
                        onClick={() => startEdit(m)}
                        className="p-1.5 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-500 hover:text-blue-600 shadow-sm"
                        title="Edit message"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-1.5 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 text-gray-500 hover:text-red-600 shadow-sm"
                      title="Delete message"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="px-4 py-3 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handlePhotoSelected}
          className="hidden"
        />
        <button
          type="button"
          onClick={handlePickPhoto}
          disabled={uploading}
          className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-slate-800 rounded-xl transition-colors flex-shrink-0"
          title="Share a photo (max 5 MB)"
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin text-blue-500" /> : <ImageIcon className="w-5 h-5" />}
        </button>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend(e);
            }
          }}
          rows={1}
          maxLength={5000}
          placeholder="Type a message..."
          className="flex-1 resize-none px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-xl text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-slate-800 dark:text-white"
        />
        <button
          type="submit"
          disabled={sending || !draft.trim()}
          className="p-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 text-white rounded-xl transition-colors flex-shrink-0"
          title="Send"
        >
          {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      </form>
    </div>
  );
};

export default ConversationThread;
